import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useProjects } from "../context/ProjectContext";
import { useToast } from "../context/ToastContext";
import { Logo } from "./Logo";
import { Modal } from "./Modal";
import { IS_MOCK } from "../api/client";
import "../styles/dashboard.css";

function ProjectSwitcher() {
  const { projects, active, setActiveId, createProject } = useProjects();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");

  const initial = (active?.name ?? "?").charAt(0).toUpperCase();

  async function doCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await createProject(name.trim());
      toast(`Project "${name.trim()}" created`);
      setName("");
      setCreating(false);
    } catch (ex) {
      toast((ex as Error).message, "err");
    }
  }

  return (
    <div className="proj-switch">
      <button className="trigger" onClick={() => setOpen((o) => !o)}>
        <span className="avatar">{initial}</span>
        <span className="meta grow">
          <div className="nm">{active?.name ?? "No project"}</div>
          <div className="rl">{active ? `${active.role} · ${active.board_count} boards` : "—"}</div>
        </span>
        <span className="faint">▾</span>
      </button>

      {open && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 35 }} onClick={() => setOpen(false)} />
          <div className="proj-menu">
            {projects.map((p) => (
              <button
                key={p.id}
                className="item"
                data-active={p.id === active?.id}
                onClick={() => {
                  setActiveId(p.id);
                  setOpen(false);
                }}
              >
                <span className="avatar" style={{ width: 22, height: 22, fontSize: 11, borderRadius: 6 }}>
                  {p.name.charAt(0).toUpperCase()}
                </span>
                <span className="grow">{p.name}</span>
                {p.id === active?.id && <span>✓</span>}
              </button>
            ))}
            <div className="sep" />
            <button className="item" onClick={() => { setOpen(false); setCreating(true); }}>
              <span style={{ width: 22, textAlign: "center" }}>＋</span>
              <span>New project</span>
            </button>
          </div>
        </>
      )}

      {creating && (
        <Modal title="Create a project" onClose={() => setCreating(false)}>
          <form className="modal-body" onSubmit={doCreate}>
            <div className="field">
              <label htmlFor="pn">Project name</label>
              <input id="pn" className="input" autoFocus value={name}
                onChange={(e) => setName(e.target.value)} placeholder="My Game" />
              <span className="hint">Boards are namespaced per project. You can rename it later.</span>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setCreating(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={!name.trim()}>Create project</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

const NAV = [
  { to: "/app", end: true, icon: "◇", label: "Overview" },
  { to: "/app/boards", end: false, icon: "▤", label: "Leaderboards" },
  { to: "/app/players", end: false, icon: "◉", label: "Players" },
  { to: "/app/moderation", end: false, icon: "⚑", label: "Moderation" },
  { to: "/app/keys", end: false, icon: "⚿", label: "API keys" },
  { to: "/app/settings", end: false, icon: "⚙", label: "Settings" },
];

export function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  async function doLogout() {
    await logout();
    nav("/");
  }

  return (
    <div className="dash">
      <aside className="side">
        <div className="side-top">
          <Link to="/"><Logo size={24} /></Link>
        </div>
        <ProjectSwitcher />

        <nav className="side-nav">
          <div className="side-section">Manage</div>
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => (isActive ? "active" : "")}>
              <span className="ic">{n.icon}</span>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="side-foot">
          {IS_MOCK && (
            <div className="tag tag-coral" style={{ marginBottom: 12 }}>
              ● mock mode — no backend
            </div>
          )}
          <div className="between">
            <div className="user-pill">
              <span className="ava">{(user?.name ?? "?").charAt(0).toUpperCase()}</span>
              <div style={{ lineHeight: 1.2, overflow: "hidden" }}>
                <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 130 }}>
                  {user?.name}
                </div>
                <div className="faint" style={{ fontSize: 11 }}>{user?.email}</div>
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={doLogout} title="Sign out">⏻</button>
          </div>
        </div>
      </aside>

      <div className="main">
        <Outlet />
      </div>
    </div>
  );
}
